import { dateTypeMap } from '@op/constants';
import { useCancelRequestHook } from '@op/hooks/useCancelRequest';
import {
  getVoiceReportingTextAnalysisDetails,
  getVoiceReportingTextAnalysisStats,
  getVoiceReportingTextAnalysisTimeline,
} from '@op/services/voiceReportingTextAnalysis';
import {
  addWithoutSingleData,
  arrayToObj,
  getRationPercentValue,
  getTimeSpaceArray,
  sortTimeListFunc,
} from '@op/utils';
import { DBO_ARR, DBO_REQ } from '@op/utils/format';
import moment from 'moment';
import { useEffect, useState } from 'react';

export function useVoiceReportingTextAnalysisStats({ filter, extraData }: any) {
  const [records, setRecords] = useState<any>({});
  const [error, setError] = useState(false);
  const { loading, wrapperRequest } = useCancelRequestHook();
  function requestStats() {
    if (!filter.edition_unified_id || !filter.start_time || !filter.end_time)
      return;
    setError(false);
    wrapperRequest((cancelFunc) =>
      getVoiceReportingTextAnalysisStats(filter, cancelFunc)
        .then((data: any) => {
          const formattedData = dealWithStats(data, filter, extraData);
          setRecords(formattedData);
        })
        .catch(() => {
          setError(true);
        }),
    );
  }

  useEffect(() => {
    requestStats();
  }, [filter]);

  return { data: records, loading, error, refetch: requestStats };
}

const dealWithStats = (data: any, filter: any, extraData: any) => {
  const {
    selected_stats = {},
    link_stats = {},
    spl_date_type_stats = {},
  } = data || {};
  const { beforeText, sameDayLastWeek } = extraData;
  const {
    count: selectedCount = 0,
    start_time: selectedStartTime,
    end_time: selectedEndTime,
    timeline_of_start_time: selectedStartTimeline,
    timeline_of_end_time: selectedEndTimeline,
    timeline: selectedTimeline,
  } = selected_stats;

  const {
    count: linkCount,
    start_time: linkStartTime,
    end_time: linkEndTime,
    timeline_of_start_time: linkStartTimeline,
    timeline_of_end_time: linkEndTimeline,
    timeline: linkTimeline,
  } = link_stats;

  const {
    count: splCount,
    start_time: splStartTime,
    end_time: splEndTime,
    timeline_of_start_time: splStartTimeline,
    timeline_of_end_time: splEndTimeline,
    timeline: splTimeline,
  } = spl_date_type_stats;

  const selectedList = addWithoutSingleData(
    selectedStartTimeline,
    selectedEndTimeline,
    filter.date_type,
    selectedTimeline,
    {
      count: 0,
    },
  );

  const splList = addWithoutSingleData(
    splStartTimeline,
    splEndTimeline,
    filter.date_type,
    splTimeline || [],
    {
      count: 0,
    },
  );

  const dodRatio = getRationPercentValue(selectedCount, linkCount, 6);
  const sdlwRatio = getRationPercentValue(selectedCount, splCount, 6);

  const baseTime = [selectedStartTime, selectedEndTime];

  return {
    selectedCount,
    selectedTime: baseTime,
    dodValue: linkCount,
    dodTime: [linkStartTime, linkEndTime],
    sdlwValue: splCount,
    sdlwTime: [splStartTime, splEndTime],
    dodRatio,
    sdlwRatio,
    xAxisData: selectedList.map((val) => val.time),
    seriesData: [
      {
        name: beforeText || 'Today', // TODO: 环比怎么展示
        data: selectedList.map((val) => val.count),
      },
      {
        name: sameDayLastWeek || 'SDLW',
        data: splList.map((val) => val.count),
      },
    ],
  };
};

export function useVoiceReportingTextAnalysisTimeline({
  filter,
  extraData,
}: any) {
  const [records, setRecords] = useState<any>({ isEmpty: true });
  const [error, setError] = useState(false);
  const { loading, wrapperRequest } = useCancelRequestHook();
  function requestTimeline() {
    if (!filter.edition_unified_id || !filter.start_time || !filter.end_time)
      return;
    setError(false);
    wrapperRequest((cancelFunc) =>
      getVoiceReportingTextAnalysisTimeline(filter, cancelFunc)
        .then((data: any) => {
          const formattedData = dealWithTimelineData(data, filter, extraData);

          setRecords(formattedData);
        })
        .catch(() => {
          setError(true);
        }),
    );
  }

  useEffect(() => {
    requestTimeline();
  }, [filter]);

  return { data: records, loading, error, refetch: requestTimeline };
}

const dealWithTimelineData = (data: any, filter: any, extraData: any) => {
  const { timeline, timeline_of_start_time, timeline_of_end_time, list } =
    data || {};

  const moreKeysData =
    DBO_REQ.moreKeys({
      timeline,
      timeline_of_start_time,
      timeline_of_end_time,
      filter,
      needTable: true,
    }) || {};

  const tableRecord =
    timeline?.map((val) => {
      let total = 0;

      Object.keys(val || {}).forEach((key) => {
        if (key !== 'time') {
          total += Number(val[key]) || 0;
        }
      });

      return { total, ...val };
    }) || [];

  return {
    ...moreKeysData,
    ...getPieData(list),
    tableRecord: tableRecord.sort(DBO_ARR.sortByTime('time', 'desc')),
  };
};

const getPieData = (list) => {
  let pieTotalData = 0;
  const pieData =
    list?.map((val) => {
      const { intent, count } = val;
      pieTotalData += count;
      return {
        name: intent,
        value: count,
        key: intent,
      };
    }) || [];

  return { pieData, pieTotalData };
};

export function useVoiceReportingTextAnalysisDetails({
  filter,
  extraData,
}: any) {
  const [records, setRecords] = useState<any>({});
  const [error, setError] = useState(false);
  const { loading, wrapperRequest } = useCancelRequestHook();
  function requestStats() {
    if (!filter.edition_unified_id || !filter.start_time || !filter.end_time)
      return;
    setError(false);
    wrapperRequest((cancelFunc) =>
      getVoiceReportingTextAnalysisDetails(filter, cancelFunc)
        .then((data: any) => {
          // const formattedData = dealWithStats(
          //   data,
          //   filter,
          //   extraData,
          // );
          setRecords(data);
        })
        .catch(() => {
          setError(true);
        }),
    );
  }

  useEffect(() => {
    requestStats();
  }, [filter]);

  return { data: records, loading, error, refetch: requestStats };
}
